<template>
  <div class="step-page-sheet">
    <UserProfile
      v-if="authenticatedUser?.documentNumber"
      :authenticated-user="authenticatedUser"
      @update="(u) => $emit('update', u)"
      @back="$emit('changeStep', profileReturnStep)"
    />

    <div
      v-else-if="currentStep == 0"
      class="p-6 lg:px-8 lg:pt-4 lg:pb-8 flex flex-col gap-6"
    >
      <div class="flex justify-center">
        <img src="logo.png" alt="Aces Logo" class="h-8 w-14" />
      </div>
      <div
        :class="[isLoading == false ? 'opacity-100' : 'opacity-0']"
        class="flex flex-col w-full gap-3 transition duration-1000 ease-in-out text-center"
      >
        <p class="font-bold text-lg">¡Bienvenido a Suscripciones!</p>
        <p class="font-regular text-dark-gray">
          Si es tu primera vez, crea tu pedido de suscripción en 4 simples
          pasos.
        </p>
        <button
          type="button"
          class="btn-primary mt-2 lg:text-lg"
          @click="$emit('changeStep', 1)"
        >
          ¡Crea tu suscripción!
        </button>
      </div>

      <div class="border-t border-light-gray pt-6">
        <div
          :class="[isLoading == false ? 'opacity-100' : 'opacity-0']"
          class="flex flex-col w-full gap-3 transition duration-1000 ease-in-out"
        >
          <p class="font-bold text-lg text-center">¿Ya tenés un pedido?</p>
          <form
            @submit.prevent="checkUserExists(dni, selectedDocType)"
            class="flex flex-col w-full text-left"
          >
            <label class="form-label">Tipo de Documento</label>
            <Listbox v-model="selectedDocType">
              <div class="relative">
                <ListboxButton
                  :disabled="notYetAuthUser?.documentNumber"
                  class="form-listbox-btn lg:text-base"
                >
                  <span class="block truncate">{{ selectedDocType }}</span>
                  <span
                    class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  >
                    <SelectorIcon
                      class="w-5 h-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    class="absolute text-left w-full py-1 mt-1 px-2 overflow-auto bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    <ListboxOption
                      v-slot="{ active, selected }"
                      v-for="docType in docList"
                      :key="docType"
                      :value="docType"
                      as="template"
                    >
                      <li
                        :class="[
                          active ? 'text-white bg-aces' : 'text-black',
                          'cursor-default select-none relative py-2 px-12',
                        ]"
                      >
                        <span
                          :class="[
                            selected ? 'font-medium' : 'font-normal',
                            'block truncate',
                          ]"
                          >{{ docType }}</span
                        >
                        <span
                          v-if="selected"
                          class="absolute inset-y-0 left-0 flex items-center pl-3 text-aces"
                        >
                          <CheckIcon class="w-5 h-5" aria-hidden="true" />
                        </span>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <label class="form-label">Número de Documento</label>
            <input
              v-model="dni"
              type="number"
              required
              class="form-input lg:text-base"
              :disabled="notYetAuthUser?.documentNumber"
            />
            <p v-if="userNotFound" class="text-red-warning text-sm">
              No se encontró el usuario, revise el tipo y número de documento.
            </p>
            <button
              id="user"
              v-if="!notYetAuthUser?.documentNumber"
              type="submit"
              class="btn-primary mt-4 lg:text-lg"
            >
              Buscar usuario
            </button>
          </form>
          <p
            class="text-sm text-center text-red-warning m-2"
            v-if="
              notYetAuthUser?.documentNumber &&
                !notYetAuthUser.phoneNumber &&
                !canUseAnyLoginChannel
            "
          >
            *Tu teléfono está incompleto, por favor comunicate tu coordinador de
            publicaciones
          </p>
          <div
            v-else-if="
              notYetAuthUser?.documentNumber &&
                !authenticatedUser?.documentNumber &&
                !otpCodeSent &&
                otpSending
            "
            class="mx-3 py-4"
          >
            <button
              type="button"
              disabled
              class="btn-primary opacity-90 cursor-wait flex items-center justify-center gap-3"
            >
              <svg
                class="animate-spin h-5 w-5 text-white flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Enviando código...
            </button>
          </div>
          <form
            v-else-if="
              notYetAuthUser?.documentNumber &&
                !authenticatedUser?.documentNumber &&
                !otpCodeSent &&
                loginSendError
            "
            class="flex justify-center flex-col mx-3 text-left space-y-3"
          >
            <div
              class="bg-red-warning bg-opacity-10 border border-red-warning rounded-lg p-4 text-sm text-red-warning"
            >
              <p>{{ loginSendError }}</p>
            </div>
            <button
              v-if="otpMeta.canUseEmail"
              type="button"
              :disabled="otpSending"
              class="btn-primary lg:text-base disabled:opacity-60"
              @click="sendOtpByEmail"
            >
              {{
                otpSending
                  ? "Enviando..."
                  : `Enviar código por correo${
                      otpMeta.maskedEmail ? ` (${otpMeta.maskedEmail})` : ""
                    }`
              }}
            </button>
            <button
              type="button"
              class="btn-secondary lg:text-base"
              @click="resetLoginAttempt"
            >
              Volver
            </button>
          </form>
          <form
            v-else-if="
              notYetAuthUser?.documentNumber &&
                !authenticatedUser?.documentNumber &&
                otpCodeSent
            "
            @submit.prevent="checkCode(code)"
            class="flex justify-center flex-col mx-3"
          >
            <div v-if="loginPhoneWarning" class="banner-warning m-2 text-left">
              {{ loginPhoneWarning }}
            </div>
            <div
              class="text-sm text-left text-gray-500 m-2 flex flex-col space-y-2 md:space-y-0"
            >
              <p v-if="otpFlow === 'verify-phone'">
                Para usar el SMS necesitamos verificar tu teléfono. Ingresá el
                código que te enviamos por SMS {{ otpDestinationHint }}.
                <template v-if="resendSecondsLeft > 0">
                  Podés pedir otro en {{ resendSecondsLeft }}s.
                </template>
                <template v-else>
                  Si lo perdiste,
                  <a
                    href="#"
                    class="underline font-medium cursor-pointer text-hyperlink"
                    @click.prevent="resendCode"
                  >
                    reenviamos
                  </a>
                  uno nuevo.
                </template>
              </p>
              <p v-else>
                Ingresá el código enviado por
                {{ loginChannel === "EMAIL" ? "correo" : "SMS" }}
                {{ otpDestinationHint }}.
                <template v-if="resendSecondsLeft > 0">
                  Podés pedir otro en {{ resendSecondsLeft }}s.
                </template>
                <template v-else>
                  Si lo perdiste,
                  <a
                    href="#"
                    class="underline font-medium cursor-pointer text-hyperlink"
                    @click.prevent="resendCode"
                  >
                    reenviamos
                  </a>
                  uno nuevo.
                </template>
              </p>
              <p
                v-if="
                  otpFlow === 'login' &&
                    loginChannel === 'SMS' &&
                    otpMeta.canUseEmail &&
                    !otpBusy
                "
                class="text-sm"
              >
                <a
                  href="#"
                  class="underline font-medium cursor-pointer text-hyperlink"
                  @click.prevent="sendOtpByEmail"
                >
                  Enviar el código por correo
                </a>
              </p>
              <p
                v-if="
                  otpFlow === 'login' &&
                    loginChannel === 'EMAIL' &&
                    otpMeta.canUseSms &&
                    !otpBusy
                "
                class="text-sm"
              >
                <a
                  href="#"
                  class="underline font-medium cursor-pointer text-hyperlink"
                  @click.prevent="sendOtpBySms"
                >
                  Enviar el código por SMS
                </a>
              </p>
              <p
                v-if="
                  otpFlow === 'login' &&
                    loginChannel === 'EMAIL' &&
                    !otpMeta.canUseSms &&
                    canVerifyPhone(notYetAuthUser) &&
                    !otpBusy
                "
                class="text-sm"
              >
                <a
                  href="#"
                  class="underline font-medium cursor-pointer text-hyperlink"
                  @click.prevent="startPhoneVerification"
                >
                  Verificar mi teléfono para recibir el código por SMS
                </a>
              </p>
            </div>
            <div class="mb-4">
              <label class="block text-sm text-dark-gray mb-3 text-center"
                >Código</label
              >
              <Transition
                mode="out-in"
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 scale-95"
                enter-to-class="opacity-100 scale-100"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 scale-100"
                leave-to-class="opacity-0 scale-95"
              >
                <div
                  v-if="otpBusy"
                  key="skeleton"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <div class="flex gap-2 justify-center">
                    <div
                      v-for="i in 6"
                      :key="i"
                      class="w-11 h-14 rounded-lg bg-light-gray animate-pulse"
                      :style="{ animationDelay: `${i * 80}ms` }"
                    />
                  </div>
                  <p
                    class="text-sm text-dark-gray text-center mt-3 font-medium"
                  >
                    {{ otpBusyMessage }}
                  </p>
                </div>
                <div v-else key="otp">
                  <OtpInput
                    ref="otpInput"
                    v-model="code"
                    :length="6"
                    :invalid="wrongCode"
                    @complete="onOtpComplete"
                  />
                  <p
                    v-if="wrongCode"
                    class="text-red-warning text-sm text-center mt-3"
                  >
                    {{ wrongCodeMessage }}
                  </p>
                </div>
              </Transition>
            </div>
            <button
              type="submit"
              :disabled="otpBusy || code.length !== 6"
              class="btn-primary my-4 lg:text-base"
            >
              {{
                otpVerifying
                  ? "Verificando..."
                  : otpSending
                  ? "Enviando..."
                  : "Confirmar"
              }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <NewUser
      v-else-if="!authenticatedUser?.documentNumber && currentStep == 1"
      @update="(u) => $emit('update', u)"
      @changeStep="(s) => $emit('changeStep', s)"
    />
  </div>
</template>

<script>
import NewUser from "./NewUser.vue";
import UserProfile from "./profile/UserProfile.vue";
import OtpInput from "./OtpInput.vue";
import axios from "axios";
import { setSubscriberToken } from "../auth";
import {
  clearPendingOtpSession,
  loadPendingOtpSession,
  savePendingOtpSession,
} from "../otpSession";
import { ref, inject } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/vue";
import { CheckIcon, SelectorIcon } from "@heroicons/vue/solid";
import { createToast } from "mosha-vue-toastify";
import { isValidArgentinePhone } from "../utils/argentinePhone";

const INVALID_PHONE_BASE =
  "El teléfono registrado en tu cuenta no es válido para SMS (debe tener 10 dígitos, sin 0, 15, 54 ni +549).";

export default {
  components: {
    NewUser,
    UserProfile,
    OtpInput,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    SelectorIcon,
    CheckIcon,
  },
  setup(props) {
    const docList = ["DNI", "LC", "LE"];
    const isLoading = ref(false);
    const wrongCode = ref(false);
    const globalState = inject("globalState");

    const selectedDocType =
      props.authenticatedUser?.docType == null
        ? ref(docList[0])
        : ref(props.authenticatedUser?.docType);

    return {
      docList,
      selectedDocType,
      isLoading,
      wrongCode,
      globalState,
    };
  },
  props: {
    authenticatedUser: {
      type: Object,
      default: () => {},
    },
    currentStep: {
      type: Number,
      default: 0,
    },
    profileReturnStep: {
      type: Number,
      default: 2,
    },
  },
  emits: ["update", "changeStep"],
  computed: {
    otpDestinationHint() {
      const m =
        this.loginChannel === "EMAIL"
          ? this.otpMeta.maskedEmail
          : this.otpMeta.maskedPhone;
      if (m && String(m).trim()) {
        return ` (${m})`;
      }
      const p = this.notYetAuthUser?.phoneNumber;
      if (this.loginChannel !== "EMAIL" && p && String(p).length > 4) {
        return ` (...${String(p).substring(String(p).length - 4)})`;
      }
      return "";
    },
    wrongCodeMessage() {
      return this.loginChannel === "EMAIL"
        ? "El código no coincide con el enviado por correo."
        : "El código no coincide con el enviado por SMS.";
    },
    canUseAnyLoginChannel() {
      const u = this.notYetAuthUser;
      if (!u) {
        return false;
      }
      const phoneVerified = !!(u.phoneVerified ?? u.PhoneVerified);
      const emailVerified = !!(u.emailVerified ?? u.EmailVerified);
      return phoneVerified || emailVerified;
    },
    otpBusy() {
      return this.otpVerifying || this.otpSending;
    },
    otpBusyMessage() {
      if (this.otpVerifying) return "Validando...";
      return "Enviando código...";
    },
  },
  data() {
    return {
      dni: "",
      code: "",
      notYetAuthUser: {},
      userNotFound: false,
      timeOut: false,
      resendSecondsLeft: 0,
      resendTimerId: null,
      otpSending: false,
      otpVerifying: false,
      loginChannel: "SMS",
      otpFlow: "login",
      otpMeta: {
        maskedPhone: "",
        maskedEmail: "",
        canUseSms: false,
        canUseEmail: false,
      },
      otpCodeSent: false,
      loginSendError: "",
      loginPhoneWarning: "",
    };
  },
  created() {
    this.restorePendingOtpSession();
  },
  beforeUnmount() {
    this.clearResendCooldown();
  },
  methods: {
    normStr(v) {
      return v == null || v === "" ? "" : String(v).trim();
    },
    normalizeClientPayload(u) {
      if (!u || typeof u !== "object") return {};
      return {
        ...u,
        clientId: u.clientId ?? u.ClientId,
        churchClientId:
          u.churchClientId ?? u.ChurchClientID ?? u.ChurchClientId,
        email: u.email ?? u.Email ?? "",
        phoneNumber: u.phoneNumber ?? u.PhoneNumber ?? "",
        emailVerified: u.emailVerified ?? u.EmailVerified ?? false,
        phoneVerified: u.phoneVerified ?? u.PhoneVerified ?? false,
      };
    },
    restorePendingOtpSession() {
      if (this.authenticatedUser?.documentNumber) {
        return;
      }
      const pending = loadPendingOtpSession();
      if (!pending?.user) {
        return;
      }
      this.notYetAuthUser = this.normalizeClientPayload(pending.user);
      this.loginChannel = pending.loginChannel || "SMS";
      this.otpFlow = pending.otpFlow || "login";
      this.otpMeta = pending.otpMeta || {
        maskedPhone: "",
        maskedEmail: "",
        canUseSms: false,
        canUseEmail: false,
      };
      this.otpCodeSent = !!pending.otpCodeSent;
      this.loginSendError = pending.loginSendError || "";
      this.loginPhoneWarning = pending.loginPhoneWarning || "";
      if (pending.docType) {
        this.selectedDocType = pending.docType;
      }
      if (pending.documentNumber) {
        this.dni = String(pending.documentNumber);
      }
    },
    persistPendingOtpSession() {
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      if (!user.clientId) {
        return;
      }
      savePendingOtpSession({
        user,
        loginChannel: this.loginChannel,
        otpFlow: this.otpFlow,
        otpMeta: { ...this.otpMeta },
        otpCodeSent: this.otpCodeSent,
        loginSendError: this.loginSendError,
        loginPhoneWarning: this.loginPhoneWarning,
        docType: user.docType || this.selectedDocType,
        documentNumber: user.documentNumber || this.dni,
      });
    },
    startResendCooldown(seconds = 30) {
      this.clearResendCooldown();
      this.timeOut = true;
      this.resendSecondsLeft = seconds;
      this.resendTimerId = setInterval(() => {
        this.resendSecondsLeft -= 1;
        if (this.resendSecondsLeft <= 0) {
          this.clearResendCooldown();
        }
      }, 1000);
    },
    clearResendCooldown() {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId);
      }
      this.resendTimerId = null;
      this.timeOut = false;
      this.resendSecondsLeft = 0;
    },
    buildNoChannelMessage(user) {
      const u = this.normalizeClientPayload(user);
      const suffix = " Contactá a tu coordinador para regularizar tus datos.";
      if (u.emailVerified && this.normStr(u.email)) {
        return (
          "No hay teléfono verificado para enviar el código por SMS. " +
          "Podés solicitar el código por correo con el botón de abajo." +
          suffix
        );
      }
      return (
        "No hay teléfono ni correo verificado para enviar el código." + suffix
      );
    },
    canVerifyPhone(user) {
      const u = this.normalizeClientPayload(user);
      return !u.phoneVerified && !!this.normStr(u.phoneNumber);
    },
    hasInvalidStoredPhone(user) {
      const u = this.normalizeClientPayload(user);
      return (
        u.phoneVerified &&
        !!this.normStr(u.phoneNumber) &&
        !isValidArgentinePhone(u.phoneNumber)
      );
    },
    getInvalidPhoneWarningMessage(canUseEmail) {
      if (canUseEmail) {
        return `${INVALID_PHONE_BASE} Podés corregirlo en tu perfil.`;
      }
      return `${INVALID_PHONE_BASE} Contactá a tu coordinador de publicaciones para corregirlo.`;
    },
    notifyLoginSendFailure(channel, detailedMessage) {
      this.loginSendError = detailedMessage;
      const ch = String(channel || "SMS").toUpperCase();
      const toastTitle =
        ch === "EMAIL" ? "Error al enviar el correo" : "Error al enviar el SMS";
      createToast(toastTitle, {
        position: "top-center",
        type: "danger",
        transition: "zoom",
      });
    },
    applyPhoneWarningIfNeeded(user, channel) {
      if (
        String(channel || "").toUpperCase() === "EMAIL" &&
        this.hasInvalidStoredPhone(user)
      ) {
        this.loginPhoneWarning = this.getInvalidPhoneWarningMessage(
          this.otpMeta.canUseEmail,
        );
      } else {
        this.loginPhoneWarning = "";
      }
    },
    applyOtpMetaFromResponse(data) {
      this.otpMeta = {
        maskedPhone: data?.maskedPhone || "",
        maskedEmail: data?.maskedEmail || "",
        canUseSms: !!data?.canUseSms,
        canUseEmail: !!data?.canUseEmail,
      };
    },
    resetLoginAttempt() {
      this.notYetAuthUser = {};
      this.otpCodeSent = false;
      this.loginSendError = "";
      this.loginPhoneWarning = "";
      this.otpSending = false;
      this.code = "";
      this.wrongCode = false;
      this.clearResendCooldown();
      clearPendingOtpSession();
    },
    async requestSubscriberLoginCode(user, channel) {
      const docType = user.docType || this.selectedDocType;
      const documentNumber = String(
        user.documentNumber ?? this.dni ?? "",
      ).trim();
      this.otpSending = true;
      try {
        const { data } = await axios.post("subscriber-auth/login/send-code", {
          docType,
          documentNumber,
          channel: String(channel || "SMS").toUpperCase(),
        });
        if (data.success) {
          this.loginChannel = String(channel || "SMS").toUpperCase();
          this.otpFlow = "login";
          this.applyOtpMetaFromResponse(data);
          this.applyPhoneWarningIfNeeded(user, channel);
          this.otpCodeSent = true;
          this.loginSendError = "";
          this.wrongCode = false;
          this.persistPendingOtpSession();
          return true;
        }
        this.applyOtpMetaFromResponse(data);
        this.otpCodeSent = false;
        const detail = data.errorMessage || "No se pudo enviar el código.";
        this.persistPendingOtpSession();
        this.notifyLoginSendFailure(channel, detail);
        return false;
      } catch (err) {
        const body = err.response?.data;
        const backendMsg =
          (typeof body === "string" && body) ||
          body?.errorMessage ||
          body?.title ||
          "No se pudo enviar el código.";
        this.otpCodeSent = false;
        if (body && typeof body === "object") {
          this.applyOtpMetaFromResponse(body);
        }
        this.persistPendingOtpSession();
        this.notifyLoginSendFailure(channel, backendMsg);
        return false;
      } finally {
        this.otpSending = false;
      }
    },
    async requestPhoneVerificationCode(user) {
      const u = this.normalizeClientPayload(user);
      if (!u?.clientId) {
        return false;
      }
      const docType = u.docType || this.selectedDocType;
      const documentNumber = String(u.documentNumber ?? this.dni ?? "").trim();
      this.otpSending = true;
      try {
        const { data } = await axios.post(
          "subscriber-auth/phone/send-verification-code",
          {
            clientId: u.clientId,
            docType,
            documentNumber,
          },
        );
        if (data.success) {
          this.loginChannel = "SMS";
          this.otpFlow = "verify-phone";
          this.applyOtpMetaFromResponse({
            ...data,
            canUseEmail: this.otpMeta.canUseEmail,
            canUseSms: false,
            maskedEmail: this.otpMeta.maskedEmail,
          });
          this.otpCodeSent = true;
          this.loginSendError = "";
          this.wrongCode = false;
          this.persistPendingOtpSession();
          return true;
        }
        this.applyOtpMetaFromResponse(data);
        this.otpCodeSent = false;
        const detail = data.errorMessage || "No se pudo enviar el código.";
        this.persistPendingOtpSession();
        this.notifyLoginSendFailure("SMS", detail);
        return false;
      } catch (err) {
        const body = err.response?.data;
        const msg =
          (typeof body === "string" && body) ||
          body?.errorMessage ||
          body?.title ||
          "No se pudo enviar el código.";
        this.otpCodeSent = false;
        if (body && typeof body === "object") {
          this.applyOtpMetaFromResponse(body);
        }
        this.loginSendError = msg;
        this.persistPendingOtpSession();
        this.notifyLoginSendFailure("SMS", msg);
        return false;
      } finally {
        this.otpSending = false;
      }
    },
    async sendOtpByEmail() {
      if (this.otpSending || this.resendSecondsLeft > 0) {
        return;
      }
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      if (!user?.clientId) {
        return;
      }
      const ok = await this.requestSubscriberLoginCode(user, "EMAIL");
      if (ok) {
        this.startResendCooldown();
        createToast("Te enviamos el código por correo.", {
          position: "top-center",
          type: "success",
          transition: "zoom",
        });
      }
    },
    async sendOtpBySms() {
      if (this.otpSending || this.resendSecondsLeft > 0) {
        return;
      }
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      if (!user?.clientId) {
        return;
      }
      const ok = await this.requestSubscriberLoginCode(user, "SMS");
      if (ok) {
        this.startResendCooldown();
        createToast("Te enviamos el código por SMS.", {
          position: "top-center",
          type: "success",
          transition: "zoom",
        });
      }
    },
    async startPhoneVerification() {
      if (this.otpSending || this.resendSecondsLeft > 0) {
        return;
      }
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      const ok = await this.requestPhoneVerificationCode(user);
      if (ok) {
        this.startResendCooldown();
        createToast(
          "Te enviamos un código por SMS para verificar el teléfono.",
          {
            position: "top-center",
            type: "success",
            transition: "zoom",
          },
        );
      }
    },
    async checkUserExists(dni, type) {
      this.userNotFound = false;
      this.clearResendCooldown();
      this.otpCodeSent = false;
      this.loginSendError = "";
      this.loginPhoneWarning = "";
      const res = await axios
        .get(`Clients/searchClient?dni=${dni}&docType=${type}`)
        .catch((err) => {
          if (err.response?.status) {
            createToast("El usuario no existe.", {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
            this.userNotFound = true;
          }
        });
      if (res && res.status === 200) {
        const user = this.normalizeClientPayload(res.data);
        this.notYetAuthUser = res.data;
        this.applyOtpMetaFromResponse({
          maskedPhone: user.phoneNumber
            ? `***${String(user.phoneNumber).slice(-4)}`
            : "",
          maskedEmail: user.email || "",
          canUseSms: !!(user.phoneVerified && user.phoneNumber),
          canUseEmail: !!(user.emailVerified && user.email),
        });

        if (user.phoneVerified && this.normStr(user.phoneNumber)) {
          const sent = await this.requestSubscriberLoginCode(user, "SMS");
          if (sent) {
            this.startResendCooldown();
            createToast("Te enviamos un código por SMS.", {
              position: "top-center",
              type: "success",
              transition: "zoom",
            });
          }
          return;
        }

        if (this.canVerifyPhone(user) && this.normStr(user.phoneNumber)) {
          const sent = await this.requestPhoneVerificationCode(user);
          if (sent) {
            this.startResendCooldown();
            createToast(
              "Verificá tu teléfono: te enviamos un código por SMS.",
              {
                position: "top-center",
                type: "info",
                transition: "zoom",
              },
            );
          }
          return;
        }

        this.otpCodeSent = false;
        this.loginSendError = this.buildNoChannelMessage(user);
        this.persistPendingOtpSession();
        return;
      }
      this.userNotFound = false;
    },
    onOtpComplete(code) {
      const trimmed = String(code ?? this.code ?? "").trim();
      if (!this.otpBusy && trimmed.length === 6) {
        this.checkCode(trimmed);
      }
    },
    async checkCode(code) {
      this.wrongCode = false;
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      const clientId = user.clientId;
      if (!clientId) {
        createToast("Sesión incompleta. Buscá el usuario de nuevo.", {
          position: "top-center",
          type: "danger",
          transition: "zoom",
        });
        return;
      }
      const trimmed = String(code ?? this.code ?? "").trim();
      if (!trimmed) {
        createToast("Ingresá el código.", {
          position: "top-center",
          type: "warning",
          transition: "zoom",
        });
        return;
      }
      if (this.otpFlow === "verify-phone") {
        await this.verifyPhoneCode(user, trimmed);
        return;
      }
      this.otpVerifying = true;
      try {
        const { data } = await axios.post("subscriber-auth/login/verify-code", {
          clientId,
          code: trimmed,
        });
        if (data.success && data.token) {
          clearPendingOtpSession();
          this.clearResendCooldown();
          await setSubscriberToken(data.token);
          if (this.globalState) {
            this.globalState.tokenUpdated = true;
          }
          const payload = this.normalizeClientPayload({
            ...user,
          });
          this.$emit("update", payload);
          this.notYetAuthUser = {};
          this.code = "";
          return;
        }
        this.wrongCode = true;
        if (data.errorMessage) {
          createToast(data.errorMessage, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        }
      } catch (err) {
        this.wrongCode = true;
        const body = err.response?.data;
        const msg =
          body?.errorMessage || body?.title || "Código incorrecto o expirado.";
        createToast(msg, {
          position: "top-center",
          type: "danger",
          transition: "zoom",
        });
      } finally {
        this.otpVerifying = false;
      }
    },
    async verifyPhoneCode(user, trimmedCode) {
      const docType = user.docType || this.selectedDocType;
      const documentNumber = String(
        user.documentNumber ?? this.dni ?? "",
      ).trim();
      this.otpVerifying = true;
      try {
        const { data } = await axios.post("subscriber-auth/phone/verify-code", {
          clientId: user.clientId,
          docType,
          documentNumber,
          code: trimmedCode,
        });
        if (data.success && data.token) {
          this.code = "";
          this.wrongCode = false;
          clearPendingOtpSession();
          this.clearResendCooldown();
          await setSubscriberToken(data.token);
          if (this.globalState) {
            this.globalState.tokenUpdated = true;
          }
          const payload = this.normalizeClientPayload({
            ...user,
            phoneVerified: true,
          });
          this.$emit("update", payload);
          this.notYetAuthUser = {};
          createToast("¡Teléfono verificado!", {
            position: "top-center",
            type: "success",
            transition: "zoom",
          });
          return;
        }
        this.wrongCode = true;
        if (data.errorMessage) {
          createToast(data.errorMessage, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        }
      } catch (err) {
        this.wrongCode = true;
        const body = err.response?.data;
        const msg =
          body?.errorMessage || body?.title || "Código incorrecto o expirado.";
        createToast(msg, {
          position: "top-center",
          type: "danger",
          transition: "zoom",
        });
      } finally {
        this.otpVerifying = false;
      }
    },
    async resendCode() {
      if (this.resendSecondsLeft > 0 || this.otpBusy) {
        return;
      }
      const user = this.normalizeClientPayload(this.notYetAuthUser);
      if (!user?.clientId) {
        createToast("Buscá el usuario de nuevo.", {
          position: "top-center",
          type: "warning",
          transition: "zoom",
        });
        return;
      }
      this.code = "";
      this.wrongCode = false;
      const ok =
        this.otpFlow === "verify-phone"
          ? await this.requestPhoneVerificationCode(user)
          : await this.requestSubscriberLoginCode(
              user,
              this.loginChannel || "SMS",
            );
      if (ok) {
        this.startResendCooldown();
        this.$nextTick(() => this.$refs.otpInput?.restartWebOtp?.());
        createToast("Código reenviado.", {
          position: "top-center",
          type: "success",
          transition: "zoom",
        });
      }
    },
  },
};
</script>
